import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiCheck, FiAlertCircle } from "react-icons/fi";
import LogoIcon from "../../imgs/ico.png";
import api from "../../services/api";

const STATUS = {
  LOADING: "loading",
  VALID: "valid",
  EXPIRED: "expired",
  INVALID: "invalid",
  SUCCESS: "success",
};

export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [status, setStatus] = useState(STATUS.LOADING);
  const [inviteData, setInviteData] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus(STATUS.INVALID);
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.purpose !== "user_invite") {
        setStatus(STATUS.INVALID);
        return;
      }
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        setStatus(STATUS.EXPIRED);
        return;
      }
      setInviteData({ email: payload.email });
      setStatus(STATUS.VALID);
    } catch (e) {
      setStatus(STATUS.INVALID);
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Informe seu nome completo");
      return;
    }
    if (password.length < 8) {
      setError("A senha deve ter no mínimo 8 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      setError("As senhas não conferem");
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post("/users/accept-invite", {
        token,
        name: name.trim(),
        password,
      });

      if (response.data.success) {
        // Salvar no formato correto para authService
        const authData = {
          token: response.data.token,
          email: response.data.user?.email || inviteData?.email,
        };
        localStorage.setItem("vidal_auth", JSON.stringify(authData));
        localStorage.setItem("vidal_user", JSON.stringify(response.data.user));
        if (response.data.projectId) {
          localStorage.setItem("vidal_selected_project", response.data.projectId);
        }
        setStatus(STATUS.SUCCESS);
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Erro ao ativar conta. Tente novamente.";
      if (msg.includes("expirado")) {
        setStatus(STATUS.EXPIRED);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (status === STATUS.LOADING) {
    return (
      <PageContainer>
        <Card>
          <StateContainer>
            <Spinner />
            <StateText>Verificando convite...</StateText>
          </StateContainer>
        </Card>
      </PageContainer>
    );
  }

  // Invalid
  if (status === STATUS.INVALID) {
    return (
      <PageContainer>
        <Card>
          <StateContainer>
            <FiAlertCircle size={48} color="#ef4444" />
            <StateTitle>Convite Inválido</StateTitle>
            <StateText>Este link de convite não é válido. Verifique se o link está correto ou solicite um novo convite.</StateText>
            <ActionButton onClick={() => navigate("/")}>Ir para o Login</ActionButton>
          </StateContainer>
        </Card>
      </PageContainer>
    );
  }

  // Expired
  if (status === STATUS.EXPIRED) {
    return (
      <PageContainer>
        <Card>
          <StateContainer>
            <FiAlertCircle size={48} color="#ef4444" />
            <StateTitle>Convite Expirado</StateTitle>
            <StateText>Este convite expirou. Solicite um novo convite ao administrador do projeto.</StateText>
            <ActionButton onClick={() => navigate("/")}>Ir para o Login</ActionButton>
          </StateContainer>
        </Card>
      </PageContainer>
    );
  }

  // Success
  if (status === STATUS.SUCCESS) {
    return (
      <PageContainer>
        <Card>
          <StateContainer>
            <SuccessIcon><FiCheck /></SuccessIcon>
            <StateTitle>Conta Ativada!</StateTitle>
            <StateText>Sua conta foi configurada com sucesso. Redirecionando...</StateText>
          </StateContainer>
        </Card>
      </PageContainer>
    );
  }

  // Valid — Form
  return (
    <PageContainer>
      <Card>
        <Header>
          <Logo>
            <img src={LogoIcon} alt="Vidal" />
          </Logo>
          <Title>Bem-vindo ao Vidal</Title>
          <Subtitle>Configure sua conta para começar</Subtitle>
          {inviteData?.email && (
            <EmailBadge>{inviteData.email}</EmailBadge>
          )}
        </Header>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label>Nome completo</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Como você quer ser chamado"
              autoFocus
              autoComplete="name"
            />
          </InputGroup>

          <InputGroup>
            <Label>Crie uma senha</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
            />
          </InputGroup>

          <InputGroup>
            <Label>Confirmar senha</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repita a senha"
              autoComplete="new-password"
            />
          </InputGroup>

          {error && (
            <ErrorMessage>
              <FiAlertCircle size={14} style={{ marginRight: 6, flexShrink: 0 }} />
              {error}
            </ErrorMessage>
          )}

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? "Ativando..." : "Ativar minha conta"}
          </SubmitButton>
        </Form>

        <Footer>
          Ao ativar, você concorda com os{" "}
          <a href="/termos">Termos de Uso</a> e{" "}
          <a href="/privacidade">Política de Privacidade</a>
        </Footer>
      </Card>
    </PageContainer>
  );
}

/* ─── Styled Components (matching login/styles.js) ─── */

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0d0f14 0%, #12141a 50%, #0d0f14 100%);
  position: relative;
  overflow: hidden;
  padding: 20px;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 50%);
    animation: rotate 20s linear infinite;
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  background: rgba(18, 20, 26, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 36px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5),
              0 0 100px rgba(16, 185, 129, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 1;
  animation: slideUp 0.5s ease-out;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 6px; }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 480px) {
    padding: 28px 24px;
    border-radius: 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Logo = styled.div`
  margin: 0 auto 24px;
  width: 48px;
  height: 48px;
  animation: float 3s ease-in-out infinite;

  img {
    width: 48px;
    height: 48px;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  line-height: 1.5;
`;

const EmailBadge = styled.div`
  display: inline-block;
  margin-top: 16px;
  padding: 8px 16px;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 100px;
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.3px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #ffffff;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.08);
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #ef4444;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: shake 0.3s ease;

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 12px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  margin-top: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }
`;

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 32px 0;
`;

const StateTitle = styled.h2`
  margin: 0;
  color: #ffffff;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.3px;
`;

const StateText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  line-height: 1.6;
  max-width: 320px;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: rgba(16, 185, 129, 0.15);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #10b981;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.25);

  a {
    color: rgba(255, 255, 255, 0.4);
    text-decoration: none;

    &:hover {
      color: rgba(255, 255, 255, 0.7);
    }
  }
`;
